import { useParams, Link } from 'react-router-dom';
import { getArticleBySlug } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { useEffect, useMemo } from 'react';
import SEOHead from '@/components/base/SEOHead';

export default function ArticleDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');
  const category = article ? categories.find((c) => c.slug === article.categorySlug) : null;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const schema = useMemo(() => {
    if (!article) return null;
    const baseUrl = 'https://readdy.ai';
    const catSlug = article.categorySlug;
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        author: { '@type': 'Person', name: article.author },
        datePublished: article.date,
        image: article.coverImage,
        publisher: { '@type': 'Organization', name: '裙子下面是野兽', url: baseUrl },
        mainEntityOfPage: { '@type': 'WebPage', '@id': `${baseUrl}/articles/${article.slug}` },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: baseUrl },
          { '@type': 'ListItem', position: 2, name: '全部文章', item: `${baseUrl}/articles` },
          { '@type': 'ListItem', position: 3, name: category?.name || '分类', item: `${baseUrl}/categories?tab=${catSlug}` },
          { '@type': 'ListItem', position: 4, name: article.title },
        ],
      },
    ];
  }, [article, category]);

  if (!article) {
    return (
      <main className="pt-20 md:pt-24 min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-4">文章未找到</h1>
          <Link to="/articles" className="text-primary-600 hover:text-primary-700 text-sm whitespace-nowrap">
            ← 返回文章列表
          </Link>
        </div>
      </main>
    );
  }

  const catSlug = article.categorySlug;

  return (
    <>
      <SEOHead schema={schema} />
      <main className="pt-20 md:pt-24 bg-background-50">
        <nav className="border-b border-background-200" aria-label="面包屑导航">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-3">
            <ol className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
              <li><Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">首页</Link></li>
              <li><i className="ri-arrow-right-s-line text-xs"></i></li>
              <li><Link to="/articles" className="hover:text-primary-600 transition-colors whitespace-nowrap">全部文章</Link></li>
              <li><i className="ri-arrow-right-s-line text-xs"></i></li>
              {category && (
                <>
                  <li><Link to={`/categories?tab=${catSlug}`} className="hover:text-primary-600 transition-colors whitespace-nowrap">{category.name}</Link></li>
                  <li><i className="ri-arrow-right-s-line text-xs"></i></li>
                </>
              )}
              <li><span className="text-foreground-800 truncate max-w-[200px]">{article.title}</span></li>
            </ol>
          </div>
        </nav>

        <article className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <header className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <Link
                to={`/categories?tab=${catSlug}`}
                className="px-3 py-1 bg-primary-100 text-primary-700 text-xs rounded-full hover:bg-primary-200 transition-colors whitespace-nowrap"
              >
                {article.category}
              </Link>
              <span className="text-xs text-foreground-500">{article.date}</span>
            </div>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500">
              <span>作者：{article.author}</span>
              <span>阅读时间：{article.readTime}</span>
            </div>
          </header>

          <div className="mb-10 rounded-lg overflow-hidden">
            <div className="relative w-full h-64 md:h-96">
              <img
                src={article.coverImage}
                alt={article.title}
                title={`${article.title} - 裙子下面是野兽`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div
            className="prose-content text-foreground-800 text-sm md:text-base leading-relaxed space-y-4"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-12 pt-8 border-t border-background-200">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xs text-foreground-500">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </article>
      </main>
    </>
  );
}